import { AnchorHTMLAttributes, forwardRef, ReactNode } from "react";
import styled from "styled-components";
import css from "@styled-system/css";
import { color, variant } from "styled-system";

import variants from "./variants";
import { AnalyticsProps } from "../analytics.types";

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /**
   The content of the link.
   */
  children: ReactNode;
  /**
   The variant of the link. Available options: inherit (inherits the styles from the parent component).
   */
  variant?: "inherit";
  /**
   Controls when the link should have an underline. Available options: "none" | "hover" | "always".
   */
  underline?: "none" | "hover" | "always";
  /**
   The color of the link. This property is used for overriding the inherited color.
   */
  color?: string;
  /**
   Custom css to be applied.
   */
  css?: string;

  /**
     - Used for adobe tracking. Accepts a json object with the following props:
     - eventLabel?: string; // Name assigned to the type of event you want to track for a particular web page element.
     - eventAction?: string; // Name assigned to the web page element, whose users' interaction you want to track.
     - eventSection?: string; // Name of the page section in which the element will sit.
     - eventType?: string; // Name assigned to categorise the action/label further to give more detail.
     - eventValue?: string; // The numerical value assigned to the event you want to track.

     If this property is not set (undefined) the component will not be tracked - default data attributes will be removed
     * */
  analytics?: AnalyticsProps & {
    eventLabel?: string;
  };
}

const Anchor = styled.a<LinkProps>`
  text-decoration: ${(props) =>
    props.underline === "always" ? "underline" : "none"};
  &:hover {
    cursor: pointer;
    text-decoration: ${(props) =>
      props.underline === "none" ? "none" : "underline"};
    ${css({
      color: "text-color-hover",
    })};
  }
  &:focus {
    ${css({
      color: "text-color-hover",
    })};
  }
  ${variant({
    variants,
  })}
  ${color}
    ${(props) => props.css}
`;

const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  (
    {
      children,
      variant = "inherit",
      underline = "hover",
      color,
      css,
      analytics,
      ...props
    }: LinkProps,
    ref
  ) => {
    const eventName = analytics ? "click" : undefined;
    const eventCategory = analytics ? "link" : undefined;

    return (
      <Anchor
        variant={variant}
        underline={underline}
        color={color}
        css={css}
        ref={ref}
        data-en={eventName}
        data-ec={eventCategory}
        data-el={analytics?.eventLabel}
        data-ea={analytics?.eventAction}
        data-es={analytics?.eventSection}
        data-et={analytics?.eventType}
        data-ev={analytics?.eventValue}
        {...props}
      >
        {children}
      </Anchor>
    );
  }
);

export default Link;
